export interface AccountantReportItem {
  name: string;
  description: string;
  href: string;
  icon: string;
  accent: string;
}

export interface AccountantReportCategory {
  name: string;
  description: string;
  icon: string;
  reports: AccountantReportItem[];
}

export const ACCOUNTANT_REPORT_CATEGORIES: AccountantReportCategory[] = [
  {
    name: 'Contabilidad',
    description: 'Estados, libros y balances contables de la empresa.',
    icon: 'ri-calculator-line',
    reports: [
      {
        name: 'Estados financieros',
        description: 'Balance general, resultados, costos, gastos y flujo de efectivo.',
        href: '/accounting/financial-statements',
        icon: 'ri-file-chart-line',
        accent: 'blue',
      },
      {
        name: 'Diario general',
        description: 'Consulta y exportación de los asientos registrados.',
        href: '/accounting/general-journal',
        icon: 'ri-book-2-line',
        accent: 'indigo',
      },
      {
        name: 'Mayor general',
        description: 'Movimientos y saldos detallados por cuenta contable.',
        href: '/accounting/general-ledger',
        icon: 'ri-book-open-line',
        accent: 'violet',
      },
      {
        name: 'Balanza de comprobación',
        description: 'Débitos, créditos y balances del período seleccionado.',
        href: '/accounting/trial-balance',
        icon: 'ri-scales-3-line',
        accent: 'cyan',
      },
      {
        name: 'Reporte de caja chica',
        description: 'Fondos, desembolsos y reposiciones de caja chica.',
        href: '/accounting/petty-cash/report',
        icon: 'ri-wallet-3-line',
        accent: 'sky',
      },
    ],
  },
  {
    name: 'Ventas y cartera',
    description: 'Ventas, caja y cuentas pendientes de clientes y suplidores.',
    icon: 'ri-line-chart-line',
    reports: [
      {
        name: 'Reporte de ventas',
        description: 'Ventas por período, productos, clientes y métodos de pago.',
        href: '/billing/sales-reports',
        icon: 'ri-bar-chart-box-line',
        accent: 'emerald',
      },
      {
        name: 'Cierre de caja',
        description: 'Consulta de cierres, diferencias y desglose por método de pago.',
        href: '/billing/cash-closing',
        icon: 'ri-safe-2-line',
        accent: 'amber',
      },
      {
        name: 'Comisiones',
        description: 'Comisiones y desempeño detallado por vendedor.',
        href: '/billing/commission-report',
        icon: 'ri-percent-line',
        accent: 'orange',
      },
      {
        name: 'Cuentas por cobrar',
        description: 'Antigüedad, estados de cuenta, cobranza y vencimientos.',
        href: '/accounts-receivable/reports',
        icon: 'ri-hand-coin-line',
        accent: 'teal',
      },
      {
        name: 'Cuentas por pagar',
        description: 'Antigüedad de saldos y pagos realizados a proveedores.',
        href: '/accounts-payable/reports',
        icon: 'ri-bill-line',
        accent: 'rose',
      },
    ],
  },
  {
    name: 'Operaciones',
    description: 'Existencias, bancos, activos y nómina en formato de reporte.',
    icon: 'ri-building-4-line',
    reports: [
      {
        name: 'Inventario',
        description: 'Existencias, costos y valorización por almacén.',
        href: '/inventory/reports',
        icon: 'ri-archive-stack-line',
        accent: 'green',
      },
      {
        name: 'Movimientos bancarios',
        description: 'Depósitos, cheques, transferencias, créditos y cargos.',
        href: '/banks-module/reports',
        icon: 'ri-bank-line',
        accent: 'blue',
      },
      {
        name: 'Activos fijos',
        description: 'Valor, depreciación y estado de los activos registrados.',
        href: '/fixed-assets/report',
        icon: 'ri-building-2-line',
        accent: 'slate',
      },
      {
        name: 'Nómina',
        description: 'Empleados, períodos, departamentos y análisis salarial.',
        href: '/payroll/reports',
        icon: 'ri-team-line',
        accent: 'fuchsia',
      },
    ],
  },
  {
    name: 'Impuestos y DGII',
    description: 'Reportes fiscales listos para revisar y exportar.',
    icon: 'ri-government-line',
    reports: [
      {
        name: 'Reporte 606',
        description: 'Compras de bienes y servicios del período.',
        href: '/taxes/report-606',
        icon: 'ri-file-list-3-line',
        accent: 'red',
      },
      {
        name: 'Reporte 607',
        description: 'Ventas de bienes y servicios del período.',
        href: '/taxes/report-607',
        icon: 'ri-file-list-2-line',
        accent: 'red',
      },
      {
        name: 'Reporte 608',
        description: 'Comprobantes fiscales anulados.',
        href: '/taxes/report-608',
        icon: 'ri-file-damage-line',
        accent: 'red',
      },
      {
        name: 'Reporte 623',
        description: 'Retenciones del Estado correspondientes al período.',
        href: '/taxes/report-623',
        icon: 'ri-file-shield-2-line',
        accent: 'red',
      },
      {
        name: 'Declaración IT-1',
        description: 'Resumen, declaración e historial mensual del ITBIS.',
        href: '/taxes/report-it1',
        icon: 'ri-file-chart-line',
        accent: 'purple',
      },
      {
        name: 'Reporte IR-17',
        description: 'Retenciones y retribuciones complementarias.',
        href: '/taxes/report-ir17',
        icon: 'ri-file-paper-2-line',
        accent: 'purple',
      },
      {
        name: 'Proporcionalidad ITBIS',
        description: 'Cálculo de proporcionalidad y resultados del período.',
        href: '/taxes/itbis-proportionality',
        icon: 'ri-pie-chart-2-line',
        accent: 'purple',
      },
    ],
  },
];

export const ACCOUNTANT_REPORT_PATHS = new Set(
  ACCOUNTANT_REPORT_CATEGORIES.flatMap((category) => category.reports.map((report) => report.href)),
);

export function isAccountantReportPath(pathname: string): boolean {
  const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return ACCOUNTANT_REPORT_PATHS.has(normalized);
}

